<template>
  <div class="fixed-heading scrollable">
    <div class="tw-flex flex-col gap-6 p-6">
      <div class="tw-flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold">Ecosystem Maps</h2>
          <p class="text-gray-500 text-sm mt-1">Overview of actors added to each ecosystem map.</p>
        </div>
      </div>

      <div class="bg-white rounded-lg border shadow-sm p-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Select a map</label>
        <Dropdown
          :options="mapOptions"
          v-model="selectedMapId"
          placeholder="Choose a map..."
        />
      </div>

      <template v-if="selectedMapId">
        <div v-if="loading" class="tw-flex items-center justify-center py-12 text-gray-400">
          Loading actors...
        </div>

        <template v-else>
          <div class="bg-white rounded-lg border shadow-sm">
            <div class="tw-flex items-center justify-between px-4 py-3 border-b">
              <span class="text-sm font-medium text-gray-700">
                {{ actors.length }} actor{{ actors.length !== 1 ? 's' : '' }} on this map
              </span>
              <div class="tw-flex items-center gap-2">
                <DsInput
                  v-model="search"
                  icon="search"
                  is-simplified
                  placeholder="Search actors..."
                  class="w-56"
                />
                <ds-button
                  v-if="isOwner"
                  label="Export CSV"
                  icon="download"
                  variant="rounded-minimal"
                  size="sm"
                  :disabled="filteredActors.length === 0"
                  @click="exportCsv"
                />
              </div>
            </div>

            <div v-if="filteredActors.length === 0" class="tw-flex items-center justify-center py-12 text-gray-400">
              No actors found.
            </div>

            <table v-else class="w-full text-sm">
              <thead>
              <tr class="border-b bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th class="px-4 py-3">Name</th>
                <!--                <th class="px-4 py-3">Category</th>-->
                <th class="px-4 py-3">Website</th>
                <th class="px-4 py-3">Internal Link</th>
                <th class="px-4 py-3">Added</th>
              </tr>
              </thead>
              <tbody>
              <tr
                v-for="actor in filteredActors"
                :key="actor.id"
                class="border-b last:border-0 hover:bg-gray-50"
              >
                <td class="px-4 py-3 font-medium">{{ actor.name }}</td>
                <!--                <td class="px-4 py-3 text-gray-600">{{ actor.category || '—' }}</td>-->
                <td class="px-4 py-3">
                  <a v-if="actor.url" :href="actor.url" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline truncate block max-w-xs">{{ actor.url }}</a>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="px-4 py-3">
                  <a v-if="actor.elastic_id" :href="`/actors/${actor.elastic_id}`" target="_blank" class="text-blue-600 hover:underline">View</a>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!--                <td class="px-4 py-3">
                                    <span
                                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                      :class="statusClass(actor.status)"
                                    >
                                      {{ actor.status }}
                                    </span>
                                </td>-->
                <td class="px-4 py-3 text-gray-500">{{ formatDate(actor.added_at) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </template>
      </template>

      <div v-else-if="!loading && maps.length === 0" class="tw-flex items-center justify-center py-12 text-gray-400">
        No maps available.
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { fetchEcosystemMapActors, fetchEcosystemMaps } from '@/Domain/EcosystemMap/Api/ecosystemMaps'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    name: 'EcosystemMapActors',
    components: { Dropdown, DsInput, DsButton },
    mixins: [UserMixin],
    data() {
      return {
        maps: [] as any[],
        selectedMapId: null as number | null,
        actors: [] as any[],
        loading: false,
        search: '',
      }
    },
    computed: {
      mapOptions() {
        return this.maps.map(m => ({ value: m.id, label: m.name }))
      },
      filteredActors() {
        if (!this.search.trim()) return this.actors
        const q = this.search.toLowerCase()
        return this.actors.filter(a =>
          (a.name && a.name.toLowerCase().includes(q))
        )
      },
    },
    watch: {
      selectedMapId(id) {
        if (id) this.loadActors(id)
        else this.actors = []
      },
    },
    async mounted() {
      await this.loadMaps()
    },
    methods: {
      async loadMaps() {
        try {
          const response = await fetchEcosystemMaps()
          this.maps = response.maps || []
        } catch (e) {
          console.error('Failed to load maps', e)
        }
      },
      async loadActors(mapId: number) {
        this.loading = true
        this.actors = []
        try {
          const response = await fetchEcosystemMapActors(mapId)
          this.actors = response.actors || []
        } catch (e) {
          console.error('Failed to load actors', e)
        } finally {
          this.loading = false
        }
      },
      statusClass(status: string) {
        switch (status) {
          case 'approved':
            return 'bg-green-100 text-green-800'
          case 'draft':
            return 'bg-yellow-100 text-yellow-800'
          case 'rejected':
            return 'bg-red-100 text-red-800'
          default:
            return 'bg-gray-100 text-gray-700'
        }
      },
      formatDate(date: string) {
        if (!date) return '—'
        return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      },
      exportCsv() {
        const mapName = this.maps.find(m => m.id === this.selectedMapId)?.name || 'map'
        const headers = ['Name', 'Website URL', 'Platform URL', 'Added']
        const origin = window.location.origin
        const rows = this.filteredActors.map(a => [
          a.name || '',
          a.url || '',
          a.elastic_id ? `${origin}/actors/${a.elastic_id}` : '',
          a.added_at ? new Date(a.added_at).toLocaleDateString() : '',
        ])

        const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`
        const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n')

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${mapName}-actors.csv`
        a.click()
        URL.revokeObjectURL(url)
      },
    },
  })
</script>
